/* APP SHELL — sidebar + routing. Exact port of code.txt MainApp, but navigation is
   URL-driven: every view/detail has a real path (/report/<scanId>/opportunities, …)
   so deep links and refresh work. `onNavigate(route)` receives the next route object
   and the host (main.js) updates the URL and re-renders. */

import { Card, T, MONO, FONT, escapeHtml } from "../ui.js";
import { AppLogo, BrandMark } from "../ui.js";
import { renderOverview } from "./report/overview.js";
import { renderOpportunities, renderTopicDetail } from "./report/opportunities.js";
import { renderWinners } from "./report/winners.js";
import { renderPages } from "./report/pages.js";
import { renderDfsLogs, renderReportInfo } from "./report/misc.js";

const NAV = [
  { id: "overview", label: "Overview", icon: `<rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>` },
  { id: "opps", label: "Opportunities", icon: `<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="4.5"/><circle cx="12" cy="12" r="0.5"/>` },
  { id: "winners", label: "Winners", icon: `<path d="M8 21h8"/><path d="M12 17v4"/><path d="M7 4h10v6a5 5 0 0 1-10 0V4z"/><path d="M7 6H4a1 1 0 0 0-1 1c0 2 1.5 3.5 4 3.8"/><path d="M17 6h3a1 1 0 0 1 1 1c0 2-1.5 3.5-4 3.8"/>` },
  { id: "pages", label: "Pages", icon: `<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>` },
];
const NavIcon = (d, color) => `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0">${d}</svg>`;

export function renderReport(app, { R, route, hasDfs = false, serpLogs = [], onDashboard, onOpenAccount, onNavigate }) {
  const st = {
    view: NAV.some(n => n.id === route.view) || route.view === "dfslogs" ? route.view : "overview",
    topicId: route.topicId || null,
    winnerId: route.winnerId || null,
    pageUrl: route.pageUrl || null,
  };

  const openTopic = id => onNavigate({ view: "opps", topicId: id });
  const goto = (v, id) => onNavigate({ view: v, winnerId: v === "winners" ? (id || null) : null });
  const setErrorCard = (err) => {
    console.error("[report view error]", err);
    /* the shell itself may have failed, in which case there is no #view-root to fill */
    const viewRoot = app.querySelector("#view-root") || app;
    viewRoot.innerHTML = Card(`
      <div style="font-weight:800;font-size:15px;margin-bottom:6px">This view failed to render</div>
      <div style="font-size:12.5px;color:${T.sub};line-height:1.6;margin-bottom:14px">The rest of the report still works — use the buttons below. The technical detail is in the browser console (F5 keeps you on this URL).</div>
      <button id="err-retry" style="border:1px solid ${T.line};background:${T.surface};border-radius:9px;padding:7px 14px;font-family:${FONT};font-size:12px;font-weight:700;cursor:pointer;margin-right:8px">Try again</button>
      <button id="err-home" style="border:none;background:${T.violet};color:#fff;border-radius:9px;padding:7px 14px;font-family:${FONT};font-size:12px;font-weight:700;cursor:pointer">Back to dashboard</button>`,
      { style: "padding:24px" });
    viewRoot.querySelector("#err-retry").addEventListener("click", () => draw());
    viewRoot.querySelector("#err-home").addEventListener("click", onDashboard);
  };

  function draw() {
    try {
      drawInner();
    } catch (err) {
      setErrorCard(err);
    }
  }

  function drawInner() {
    const topic = st.topicId ? R.topics.find(t => t.id === st.topicId) : null;
    const counts = { overview: null, opps: R.opportunities.length, winners: R.winners.length, pages: R.pages.filter(p => p.trad.imp > 0 || p.ai.imp > 0).length };

    app.innerHTML = `
      <div style="min-height:100vh;background:${T.bg};display:flex;font-family:${FONT};color:${T.ink}">
        <aside style="width:218px;background:${T.surface};border-right:1px solid ${T.line};padding:16px 10px;display:flex;flex-direction:column;flex-shrink:0;position:sticky;top:0;height:100vh;overflow-y:auto">
          <div style="display:flex;align-items:center;gap:8px;padding:4px 10px;margin-bottom:8px">
            ${AppLogo(26)}
            <div>
              <div style="font-size:13px;font-weight:800;letter-spacing:-0.3px;line-height:1.2"><span style="color:${T.violet}">AIO Gap</span> Finder</div>
            </div>
          </div>
          <div style="display:flex;align-items:center;gap:6px;padding:6px 10px;margin:0 4px 14px;background:${T.bg};border:1px solid ${T.line};border-radius:9px">
            <span style="width:7px;height:7px;border-radius:50%;background:${T.green};flex-shrink:0"></span>
            <span style="font-family:${MONO};font-size:10.5px;font-weight:600;color:${T.sub};overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escapeHtml(R.meta.site)}</span>
          </div>
          <nav style="display:flex;flex-direction:column;gap:2px;flex:1">
            <button data-nav-reset style="display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:9px;border:none;cursor:pointer;background:transparent;color:${T.sub};font-family:${FONT};font-size:12.5px;font-weight:600;text-align:left;margin-bottom:6px">
              ${NavIcon(`<path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V21h14V9.5"/>`, T.muted)}
              Dashboard
            </button>
            <div style="font-size:10px;font-weight:800;color:${T.blue};letter-spacing:0.8px;padding:6px 10px 3px">FINDER</div>
            ${NAV.map(n => {
              const active = st.view === n.id;
              return `
                <button data-nav="${n.id}" style="display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:9px;border:none;cursor:pointer;background:${active ? T.violetSoft : "transparent"};color:${active ? T.violet : T.sub};font-family:${FONT};font-size:12.5px;font-weight:${active ? 700 : 500};text-align:left">
                  ${NavIcon(n.icon, active ? T.violet : T.muted)}
                  <span style="flex:1">${n.label}</span>
                  ${counts[n.id] != null ? `<span style="font-family:${MONO};font-size:10px;font-weight:700;color:${active ? T.violet : T.muted};background:${active ? "transparent" : T.lineSoft};border-radius:6px;padding:1px 6px">${counts[n.id]}</span>` : ""}
                </button>`;
            }).join("")}
          </nav>
          <div style="padding-top:10px;border-top:1px solid ${T.line};margin-top:8px">
            <div style="font-size:9px;font-weight:800;color:${T.amber};letter-spacing:0.8px;padding:0 10px;margin-bottom:6px">TOOL</div>
            <button data-nav-account style="display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:9px;border:none;cursor:pointer;background:transparent;color:${T.sub};font-family:${FONT};font-size:12.5px;font-weight:500;text-align:left;width:100%">
              ${NavIcon(`<path d="M12 2v4"/><path d="M12 18v4"/><path d="M2 12h4"/><path d="M18 12h4"/><circle cx="12" cy="12" r="4"/>`, hasDfs ? T.magenta : T.muted)}
              <span style="flex:1">DataForSEO API</span>
              <span style="width:7px;height:7px;border-radius:50%;background:${hasDfs ? T.magenta : T.line};flex-shrink:0"></span>
            </button>
            <button data-nav-dfslogs style="display:flex;align-items:center;gap:8px;padding:6px 10px 6px 32px;border-radius:9px;border:none;cursor:pointer;background:${st.view === "dfslogs" ? T.magentaSoft : "transparent"};color:${st.view === "dfslogs" ? T.magenta : T.muted};font-family:${FONT};font-size:11.5px;font-weight:${st.view === "dfslogs" ? 700 : 500};text-align:left;width:100%">
              ${NavIcon(`<path d="M4 4h16v16H4z"/><path d="M8 9h8"/><path d="M8 13h8"/><path d="M8 17h5"/>`, st.view === "dfslogs" ? T.magenta : T.muted)}
              API logs
            </button>
            <button data-nav-account2 style="display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:9px;border:none;cursor:pointer;background:transparent;color:${T.sub};font-family:${FONT};font-size:12.5px;font-weight:500;text-align:left;width:100%">
              ${NavIcon(`<circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 3.6-7 8-7s8 3 8 7"/>`, T.muted)}
              Account
            </button>
            <div style="display:flex;align-items:center;gap:5px;font-size:10px;color:${T.muted};padding:10px 10px 2px">${BrandMark(13)}<span>Built by <b>Botpresso Labs</b>${R.meta.demo ? " · demo data" : ""}</span></div>
          </div>
        </aside>
        <main style="flex:1;padding:22px 26px;overflow-y:auto;min-width:0">
          <div style="margin-bottom:18px">
            <div style="font-size:22px;font-weight:800;letter-spacing:-0.5px">${st.view === "dfslogs" ? "DataForSEO API logs" : (NAV.find(n => n.id === st.view) || NAV[0]).label}</div>
            <div style="font-size:12.5px;color:${T.muted};font-family:${MONO}">${escapeHtml(R.meta.site)} · ${escapeHtml(R.meta.rangeLabel)}</div>
          </div>
          <div id="view-root"></div>
          <div id="report-info-root"></div>
        </main>
      </div>`;

    const viewRoot = app.querySelector("#view-root");
    const scrollTop = () => window.scrollTo(0, 0);
    if (st.view === "overview") renderOverview(viewRoot, { R, openTopic, goto });
    else if (st.view === "opps") {
      if (topic) renderTopicDetail(viewRoot, { R, topicId: st.topicId, onBack: () => onNavigate({ view: "opps" }) });
      else renderOpportunities(viewRoot, { R, openTopic });
    } else if (st.view === "winners") {
      renderWinners(viewRoot, { R, selected: st.winnerId, setSelected: id => onNavigate({ view: "winners", winnerId: id }), openTopic });
    } else if (st.view === "pages") {
      renderPages(viewRoot, { R, selected: st.pageUrl, setSelected: url => onNavigate({ view: "pages", pageUrl: url }), openTopic });
    } else if (st.view === "dfslogs") {
      renderDfsLogs(viewRoot, serpLogs, R.meta.serpLog);
    }
    renderReportInfo(app.querySelector("#report-info-root"), R.meta.serpLog);

    app.querySelector("[data-nav-reset]").addEventListener("click", onDashboard);
    app.querySelectorAll("[data-nav]").forEach(b => b.addEventListener("click", () => { goto(b.dataset.nav); scrollTop(); })) ;
    app.querySelector("[data-nav-dfslogs]").addEventListener("click", () => { goto("dfslogs"); scrollTop(); });
    for (const attr of ["data-nav-account", "data-nav-account2"]) {
      app.querySelector(`[${attr}]`).addEventListener("click", onOpenAccount);
    }
    scrollTop();
  }

  draw();
}
